import { RequiredFieldTooltip } from '@/shared/components/required-field-tooltip';
import { validateEmail, validatePhone } from '@/shared/utils';
import { Field, Text } from '@noorden-insurtech/alba-design-system';
import { Plus, Trash2 } from 'lucide-react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { withMask } from 'use-mask-input';

type InspectionContact = {
  name: string;
  phone: string;
  email: string;
};

type InspectionContactErrors = {
  name?: string;
  phone?: string;
  email?: string;
};

export type InspectionContactSectionHandle = {
  validate: () => boolean;
  getContacts: () => InspectionContact[];
};

const EMPTY_CONTACT: InspectionContact = { name: '', phone: '', email: '' };

type InspectionContactSectionProps = {
  disabled?: boolean;
  initialContacts?: { name: string; email: string; phone: string }[];
};

export const InspectionContactSection = forwardRef<
  InspectionContactSectionHandle,
  InspectionContactSectionProps
>(function InspectionContactSection({ disabled = false, initialContacts }, ref) {
  const [contacts, setContacts] = useState<InspectionContact[]>(() => {
    if (initialContacts && initialContacts.length > 0) {
      return initialContacts.map(c => ({
        name: c.name || '',
        phone: c.phone || '',
        email: c.email || '',
      }));
    }
    return [{ ...EMPTY_CONTACT }];
  });
  const [errors, setErrors] = useState<InspectionContactErrors[]>(() =>
    initialContacts && initialContacts.length > 0
      ? initialContacts.map(() => ({}))
      : [{}],
  );
  const [touched, setTouched] = useState<
    Record<number, Record<string, boolean>>
  >({});

  function validateSingleField(
    field: keyof InspectionContact,
    value: string,
  ): string | undefined {
    if (field === 'name') {
      if (!value.trim()) return 'Nome é obrigatório';
      if (value.trim().length < 3) return 'Nome deve ter pelo menos 3 caracteres';
    }
    if (field === 'phone') {
      if (!value.trim()) return 'Telefone é obrigatório';
      if (!validatePhone(value)) return 'Telefone inválido';
    }
    if (field === 'email') {
      if (!value.trim()) return 'E-mail é obrigatório';
      if (!validateEmail(value)) return 'E-mail inválido';
    }
    return undefined;
  }

  function updateContact(
    index: number,
    field: keyof InspectionContact,
    value: string,
  ) {
    setContacts(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    if (touched[index]?.[field]) {
      validateField(index, field, value);
    }
  }

  function validateField(
    index: number,
    field: keyof InspectionContact,
    value: string,
  ) {
    setErrors(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: validateSingleField(field, value),
      };
      return updated;
    });
  }

  function validateAll(): boolean {
    const fields: (keyof InspectionContact)[] = ['name', 'phone', 'email'];
    const newErrors: InspectionContactErrors[] = [];
    const newTouched: Record<number, Record<string, boolean>> = {};
    let hasError = false;

    for (let i = 0; i < contacts.length; i++) {
      const contactErrors: InspectionContactErrors = {};
      newTouched[i] = {};

      for (const field of fields) {
        newTouched[i][field] = true;
        const error = validateSingleField(field, contacts[i][field]);
        if (error) {
          contactErrors[field] = error;
          hasError = true;
        }
      }

      newErrors.push(contactErrors);
    }

    setErrors(newErrors);
    setTouched(prev => ({ ...prev, ...newTouched }));

    return !hasError;
  }

  useImperativeHandle(ref, () => ({
    validate: validateAll,
    getContacts: () => contacts,
  }));

  function handleBlur(index: number, field: keyof InspectionContact) {
    setTouched(prev => ({
      ...prev,
      [index]: { ...prev[index], [field]: true },
    }));
    validateField(index, field, contacts[index][field]);
  }

  function addContact() {
    setContacts(prev => [...prev, { ...EMPTY_CONTACT }]);
    setErrors(prev => [...prev, {}]);
  }

  function removeContact(index: number) {
    if (contacts.length <= 1) return;
    setContacts(prev => prev.filter((_, i) => i !== index));
    setErrors(prev => prev.filter((_, i) => i !== index));
    setTouched(prev => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  }

  return (
    <div className='space-y-4'>
      <Text className='text-lg font-bold text-alba-black-500'>
        Contato de Inspeção
      </Text>

      {contacts.map((contact, index) => (
        <div key={index} className='space-y-4'>
          {index > 0 && (
            <div className='flex items-center justify-between pt-2'>
              <hr className='flex-1 border-gray-200' />
              <button
                type='button'
                onClick={() => removeContact(index)}
                disabled={disabled}
                className='ml-3 text-alba-error-red-500 hover:text-alba-error-red-700 transition-colors disabled:opacity-50 cursor-pointer'
                title='Remover contato'
              >
                <Trash2 className='w-5 h-5' strokeWidth={1.6} />
              </button>
            </div>
          )}

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
            <RequiredFieldTooltip active>
            <Field.Root
              invalid={!!errors[index]?.name}
              disabled={disabled}
            >
              <Field.Label>
                Nome<span className='text-alba-error-red-500'>*</span>
              </Field.Label>
              <Field.Input
                value={contact.name}
                onChange={e => updateContact(index, 'name', e.target.value)}
                onBlur={() => handleBlur(index, 'name')}
                placeholder='Digite o nome do contato'
              />
              {errors[index]?.name && (
                <Field.ErrorText>{errors[index].name}</Field.ErrorText>
              )}
            </Field.Root>
            </RequiredFieldTooltip>

            <RequiredFieldTooltip active>
            <Field.Root
              invalid={!!errors[index]?.phone}
              disabled={disabled}
            >
              <Field.Label>
                Telefone/Celular
                <span className='text-alba-error-red-500'>*</span>
              </Field.Label>
              <Field.Input
                ref={withMask('(99) 99999-9999')}
                value={contact.phone}
                onChange={e => updateContact(index, 'phone', e.target.value)}
                onBlur={() => handleBlur(index, 'phone')}
                placeholder='(xx) xxxxx-xxxx'
              />
              {errors[index]?.phone && (
                <Field.ErrorText>{errors[index].phone}</Field.ErrorText>
              )}
            </Field.Root>
            </RequiredFieldTooltip>
          </div>

          <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
            <RequiredFieldTooltip active>
            <Field.Root
              invalid={!!errors[index]?.email}
              disabled={disabled}
            >
              <Field.Label>
                E-mail<span className='text-alba-error-red-500'>*</span>
              </Field.Label>
              <Field.Input
                type='email'
                value={contact.email}
                onChange={e =>
                  updateContact(index, 'email', e.target.value.toLowerCase())
                }
                onBlur={() => handleBlur(index, 'email')}
                placeholder='exemplo@email.com'
              />
              {errors[index]?.email && (
                <Field.ErrorText>{errors[index].email}</Field.ErrorText>
              )}
            </Field.Root>
            </RequiredFieldTooltip>
          </div>
        </div>
      ))}

      <button
        type='button'
        onClick={addContact}
        disabled={disabled}
        className='flex items-center gap-2 text-[#2C2864] font-semibold cursor-pointer disabled:opacity-50'
      >
        <Plus className='w-4 h-4' strokeWidth={2} />
        Adicionar Contato
      </button>
    </div>
  );
});
